/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {history as defaultHistory, Route, Router} from '@exc/router';
import {Events, markEventsPerformance} from '@exc/shared';
import ExcCore, {ErrorTypes, ErrorView, Sandbox, ServiceWorkerControllerService} from '@exc/core';
import {getPathWithoutTenant, getQueryValue} from '@exc/url';
import {Internal} from '@adobe/exc-app/internal';
import pathToRegexp, {compile} from 'path-to-regexp';
import PropTypes from 'prop-types';
import React from 'react';
import * as solutionConfigs from './solutions';
import './App.css';

export const SHELL_MARKED_EVENTS = [
  'Shell.start',
  'Shell.done',
  'Shell.isLoginFlow.done',
  'GraphQl.start',
  'GraphQl.data.initialized',
  'GraphQl.failure',
  'HeaderData.start',
  'HeaderData.data.initialized',
  'HeaderData.failure',
  'Sandbox.connected',
  'Sandbox.onMessage.ping',
  'Sandbox.reconnected',
  'Sandbox.secondPing',
  'IframeTimer.start',
  'Discovery.start',
  'Discovery.success',
  'Discovery.failure',
  'exc.metrics.pageState.load.done',
  'ServiceWorker.liveHtml',
  'SolutionSwitcher.Load',
  'Shell.notPermissioned',
  'CustomToken.start',
  'CustomToken.done'
];

export default function UnifiedShell({cdn, env, history, region, scripts, scriptsPath, solutions, spaVersion}) {
  const {hash, origin, pathname, search} = window.location;
  let metricsEnv = env = env.toLowerCase();
  history = history || defaultHistory;
  solutions = solutions || solutionConfigs;

  // If the Shell is loaded within a parent, we should send up a redirect to
  // this page that we're on. This supports redirects within other applications
  // that may end up in this situation. Additionally, don't do this if this
  // is running in a Cypress test because it will break!
  if (window !== window.parent && !window.Cypress) {
    window.parent.postMessage({
      appId: 'unified-shell',
      type: Events.QUEUE,
      unifiedShell: 1,
      value: [[Events.SHELL_REDIRECT, getPathWithoutTenant(hash.replace(/^#/, ''))]]
    }, origin);
    return null;
  }

  let localConfigs = JSON.parse(window.localStorage.getItem('unifiedShellConfig'));
  let devmodeEnabled = localConfigs && localConfigs.devmodeEnabled === true;

  if (getQueryValue('devmode') === 'true' || devmodeEnabled) {
    metricsEnv = 'dev';
    solutions = localConfigs ? {...solutions, ...localConfigs.solutions} : solutions;
  }

  Internal.configureMetrics({
    analytics: {
      script: env === 'prod' ?
        '//exc-unifiedcontent.experience.adobe.net/static/launch/launch-ENc5d896b39e2d478ea2d22b79b1ae9e91.min.js' :
        '//exc-unifiedcontent.experience-stage.adobe.net/static/launch/launch-ENecd21a85c13248a8832b233c41cf22d6-staging.min.js',
      solution: 'exc' // initial value
    },
    environment: metricsEnv
  }).then(() => {
    Internal.setApplication({
      id: 'unified-shell',
      name: 'Experience Cloud Unified Shell',
      version: spaVersion || 'local.development'
    });
  });

  // Adds a filter to Metrics which will create performance marks
  // for the events listed in SHELL_MARKED_EVENTS
  markEventsPerformance(
    SHELL_MARKED_EVENTS,
    event => 'us_' + event.toLowerCase().replace(/[ ().\-&]+/g, '_')
  );

  // Sort the solutions by number of chunks in the path. This is required to
  // handle /home and /home/notifications. The latter needs to come first so
  // that it matches correctly in the router.
  solutions = Object.values(solutions).sort((a, b) => {
    const aLen = a.path.split('/').length;
    const bLen = b.path.split('/').length;
    if (aLen < bLen) {
      return 1;
    } else if (aLen > bLen) {
      return -1;
    }
    return 0;
  });

  const serviceWorkerService = new ServiceWorkerControllerService();
  const swEnabled = getQueryValue('sw_enabled');

  if (swEnabled === 'true') {
    window.addEventListener('load', () => serviceWorkerService.registerServiceWorker());
  }

  const htmlReadyPromise = serviceWorkerService.getHtml();

  if (swEnabled === 'false') {
    serviceWorkerService.unregister(true);
  }

  // If there is a path defined, it's not valid in the Unified Shell URL. It
  // replaces the history path to `/` and updates the hash history (`history`)
  // to the provided path.
  if (history && /^(\/((?!login).){1,})$/.test(pathname)) {
    window.history.replaceState(null, null, '/');
    // The hash property defaults to `#/` since Unified Shell uses hash history.
    // Don't want this to transfer to the final path.
    history.replace({hash: hash === '#/' ? '' : hash, pathname, search});
  }

  return (
    <ExcCore
      cdn={cdn || window.location.origin}
      environment={env}
      htmlReadyPromise={htmlReadyPromise}
      listedApps={solutions}
      metricsEnv={metricsEnv}
      region={region}
      scripts={scripts}
      scriptsPath={scriptsPath}
      spaVersion={spaVersion}>
      <Router history={history}>
        {/* Handle all solutions with optional tenant and optional sandbox */}
        {Object.values(solutions).map((solution, idx) => (
          <Route key={idx} path={`/:id(@[^/]+)?/:contextParam([^/]+:[^/]+)?${solution.path}`}>
            <Sandbox config={solution} />
          </Route>
        ))}

        {/* Handle solution redirect options */}
        {[...Object.values(solutions).map((solution, idx) => (
          (solution.redirectFrom || []).map((redirect, redirectIdx) => (
            <Route exact key={`${idx}_${redirectIdx}`} path={`/:id(@[^/]+)?/:contextParam([^/]+:[^/]+)?${redirect}`} redirect={({route}) => {
              const keys = [];
              const redirectMatcher = pathToRegexp(redirect, keys);
              const matchResult = redirectMatcher.exec(getPathWithoutTenant(route));

              if (!matchResult) {
                return route.replace(redirect, solution.path);
              }

              const toNewPath = compile(solution.path, {encode: encodeURIComponent});
              const params = {};
              for (let i = 1; i < matchResult.length; i++) {
                let key = keys[i - 1];
                let val = matchResult[i];

                if (val !== undefined) {
                  params[key.name] = val;
                }
              }
              return route.replace(matchResult[0], toNewPath(params));
            }} />
          ))
        ))]}

        {/* Handle URLs that only have tenant */}
        <Route path="/@:id" redirect={({route}) => {
          return `${route}${route.endsWith('/') ? '' : '/'}home`;
        }} />

        {/* Handle Profile */}
        <Route key="profile" path="(/@|):id?/profile" />

        {/* Handle no path */}
        <Route exact path="/" redirect="/home" />
        <Route>
          <ErrorView type={ErrorTypes.NOT_FOUND} />
        </Route>
      </Router>
    </ExcCore>
  );
}

UnifiedShell.defaultProps = {
  solutions: null
};

UnifiedShell.propTypes = {
  env: PropTypes.string.isRequired,
  region: PropTypes.string,
  solutions: PropTypes.array,
  spaVersion: PropTypes.string
};
